/**
 * SunoStemsViewerSheet — просмотр дорожек стем-разделения. Смонтирован на App-
 * уровне, открывается автоматически по pendingViewer из SunoStemJobsContext.
 * Каждая дорожка (вокал/бас/драмы/инструментал…) — плеер + скачивание.
 */
import Sheet from '../../../components/Sheet'
import MediaPlayer from '../../../components/MediaPlayer'
import { Icon } from '../../../../public/icons/Icon.tsx'
import { useSunoStemJobs } from '../../../context/SunoStemJobsContext'

// Человекочитаемые имена дорожек. Ключи приходят двумя путями: из имени файла
// (Mureka) и из ответа Suno при полном разборе (vocal, drums, backingVocals…).
const LABEL_RU: Record<string, string> = {
	vocals: 'Вокал',
	vocal: 'Вокал',
	backingvocals: 'Бэк-вокал',
	instrumental: 'Минус',
	drums: 'Ударные',
	bass: 'Бас',
	other: 'Прочее',
	guitar: 'Гитара',
	piano: 'Пиано',
	keyboard: 'Клавишные',
	percussion: 'Перкуссия',
	strings: 'Струнные',
	synth: 'Синтезатор',
	brass: 'Духовые медные',
	woodwinds: 'Духовые деревянные',
	fx: 'Эффекты',
	song: 'Трек',
}

function prettyLabel(raw: string, idx: number): string {
	const key = (raw || '').toLowerCase().replace(/\.(wav|mp3|flac)$/i, '')
	return LABEL_RU[key] || raw || `Дорожка ${idx + 1}`
}

export default function SunoStemsViewerSheet() {
	const { pendingViewer, dismissViewer } = useSunoStemJobs()
	if (!pendingViewer) return null

	const { title, urls, labels, groups } = pendingViewer

	// Показываем все виды разбора, что делались для трека: полный и минусовку.
	// Каждый стоит отдельно — прятать оплаченный за самым свежим нельзя.
	// Старый бэк groups не отдаёт → показываем то, что пришло, одним блоком.
	const blocks = groups?.length
		? groups.map((g) => ({ kind: g.kind, urls: g.result_urls, labels: g.labels }))
		: [{ kind: 'stems' as const, urls, labels }]

	const downloadFile = async (url: string, filename: string) => {
		try {
			const response = await fetch(url)
			if (!response.ok) throw new Error(`HTTP ${response.status}`)
			const blob = await response.blob()
			const objectUrl = URL.createObjectURL(blob)
			const a = document.createElement('a')
			a.href = objectUrl
			a.download = filename
			document.body.appendChild(a)
			a.click()
			a.remove()
			setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000)
		} catch {
			// Файл на чужом домене без CORS читать из JS нельзя — но отдать его
			// юзеру можно: браузеру для прямой ссылки разрешение не нужно.
			const a = document.createElement('a')
			a.href = url
			a.download = filename
			a.rel = 'noopener'
			document.body.appendChild(a)
			a.click()
			a.remove()
		}
	}

	return (
		<Sheet
			open={!!pendingViewer}
			onClose={dismissViewer}
			variant="center"
			title="Стемы"
			className="suno-result-viewer"
		>
			<div className="suno-result-viewer__body">
				{title?.trim() && <div className="suno-stems-view__track">{title.trim()}</div>}
				{blocks.map((block) => (
					<div key={block.kind}>
						{blocks.length > 1 && (
							<div className="suno-stems-view__group">
								{block.kind === 'minus' ? 'Минусовка' : 'Дорожки по инструментам'}
							</div>
						)}
						<div className="suno-stems-view">
							{block.urls.map((url, i) => {
								const label = prettyLabel(block.labels[i] ?? '', i)
								return (
									<div key={url} className="suno-stems-view__item">
										<div className="suno-stems-view__label">
											<Icon id="gen-audio" size={14} />
											{label}
										</div>
										<MediaPlayer
											variant="audio"
											src={url}
											title={label}
											onDownload={() => downloadFile(url, `${label}.wav`)}
										/>
									</div>
								)
							})}
						</div>
					</div>
				))}
			</div>
		</Sheet>
	)
}
